/**
 * Pages a person reaches from an emailed link carrying its own token, with no
 * FieldIQ sign-in. The customer approving a ticket (every vertical's review
 * email links `/ticket-review/:token`) has no account and never will; the
 * invitee and the password resetter do not have a working session yet.
 *
 * WHY THIS LIST EXISTS
 *
 * The browser opening one of these links may also hold a staff session — an
 * employee checking the customer's link on their own phone, or a token left
 * behind by a JWT rotation. Seen 2026-10-22: a stale token made the review
 * link land on /login on the first click and work on the second. The chain:
 * `AuthProvider` probed /auth/me (401) → `logout()` → its push-device DELETE
 * sent the same dead token (401) → the response interceptor in `api/client.ts`
 * cleared the token and hard-navigated to /login. The second click worked only
 * because the first had already deleted the token.
 *
 * So on these paths two things never happen:
 *   - `AuthProvider` does not probe the stored session at all — the session is
 *     left exactly as it was, neither confirmed nor ended, because opening a
 *     customer's link is not a reason to sign an employee out;
 *   - the 401 interceptor does not clear the token or redirect to /login.
 *
 * The pages themselves make their requests through `api/public.ts`, which never
 * attaches the staff token. This list is the guard on everything AROUND them.
 *
 * Adding a public page to `AppRoutes` in App.tsx? Add its path here too, or a
 * stale staff session can bounce its reader to /login.
 */
// '/portal/rentals/' is the rental customer's emailed link (RentalPortalPage). The
// rest of '/portal/*' is the SIGNED-IN customer portal and is deliberately not here.
// '/quote-review/' also covers the Earthworks change-order link at
// '/quote-review/change-orders/:token' (EW-5). '/change-order-review/' is the
// same page under the path the API's email writes (ewCoReviewUrl).
// '/invoice-pay/' is the Earthworks invoice "Pay now" link (EW-6b, InvoicePayPage).
const PUBLIC_TOKEN_PATH_PREFIXES = ['/ticket-review/', '/portal/rentals/', '/quote-review/', '/change-order-review/', '/invoice-pay/'] as const;
const PUBLIC_TOKEN_PATHS = ['/invite/setup-password', '/reset-password'] as const;

export function isPublicTokenPath(pathname: string): boolean {
  return (
    PUBLIC_TOKEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    (PUBLIC_TOKEN_PATHS as readonly string[]).includes(pathname)
  );
}

/** The path the browser is on now. Read at call time, never cached. */
export function onPublicTokenPage(): boolean {
  return typeof window !== 'undefined' && isPublicTokenPath(window.location.pathname);
}
